import React, { memo, useCallback, useMemo, useState } from 'react';

import { EValidationRuleType } from '@utils/treeValidation';

import { IBasicNode } from '@interfaces/components/tree';
import { IAddXMLNode } from '@components/common/FlatXMLTree/interface';

import { ETreeTypeNode } from '@enums/tree';

import {
	ARRAY_NODE_KEY,
	FLAT_TREE_CHECKBOX_WIDTH,
	FLAT_TREE_NODE_PADDING,
	FLAT_TREE_NODE_VIRTUALIZED_DEFAULT_HEIGHT,
} from '../constants';
import { FLAT_NODE_PATH_SEPARATOR } from '@components/common/FlatTree/constants';

import {
	IFlatTreeNodeInterface,
	IFlatTreeNodeValidation,
	IFlatTreeValidationRule,
	TFlatTreeGetValidation,
} from '../interface';
import { typeOfBrackets } from '../utils';

import AddFlatNodeForm from './AddFlatNodeForm';
import EditFlatNodeForm from './EditFlatNodeForm';
import FlatTreeNode from './FlatTreeNode';
import FlatTreeNodeError from './FlatTreeNodeError';

import styles from './FlatTreeNodeWrapper.module.scss';

const ARRAYS_NODE_PARENT_INDEX = -2;

interface IFlatTreeNodeWrapperProps {
	node: IFlatTreeNodeInterface;
	onRemove: (item: IFlatTreeNodeInterface) => void;
	onSkip: (id: string) => void;
	onEdit: (id: string, value: string) => void;
	onAdd: (data: IAddXMLNode<IBasicNode>) => void;
	index: string;
	onCollapse: (id: string, path: string) => void;
	onCopy: (node: IFlatTreeNodeInterface) => void;
	getValidationRules?: TFlatTreeGetValidation;
	getChildrenNamesList?: (node: IFlatTreeNodeInterface) => string[];
	isCollapsed?: boolean;
	isReadOnly?: boolean;
	isWithVirtualization?: boolean;
	style?: React.CSSProperties;
}

const FlatTreeNodeWrapper = ({
	node,
	onRemove,
	onSkip,
	onEdit,
	onAdd,
	onCollapse,
	onCopy,
	isReadOnly,
	isWithVirtualization,
	getValidationRules,
	getChildrenNamesList,
	index,
	style,
}: IFlatTreeNodeWrapperProps) => {
	const [isEditMode, setIsEditMode] = useState(false);
	const [isAddMode, setIsAddMode] = useState(false);
	const [isError, setIsError] = useState(false);

	const [nodeValidation, setNodeValidation] = useState<IFlatTreeNodeValidation>();

	const paddingSize = FLAT_TREE_NODE_PADDING * node.depth;

	const { openedBracket, closedBracket } = typeOfBrackets(node.nodeType);

	const toggleCollapse = useCallback(() => onCollapse(index, node.path), [node.path, index, onCollapse]);

	const skipNodeHandler = useCallback(() => onSkip(index), [index, onSkip]);

	const copyNodeHandler = useCallback(() => onCopy(node), [node, onCopy]);

	const callValidation = useCallback(async (callback: (rules?: IFlatTreeValidationRule[]) => void) => {
		if (getValidationRules && node.description) {
			try {
				const nodeKey = node.key === ARRAY_NODE_KEY
					? node.path.split(FLAT_NODE_PATH_SEPARATOR).at(ARRAYS_NODE_PARENT_INDEX) || ''
					: node.key;

				const { rules, validations } = await getValidationRules({
					name: nodeKey,
					nameSpace: node.description,
					path: node.keysPath || '',
				});

				setNodeValidation(validations);
				callback(rules);
			} catch {
				setNodeValidation(undefined);
			}
		} else if (!getValidationRules) {
			callback();
		}
	}, [
		getValidationRules,
		node.description,
		node.key,
		node.path,
		node.keysPath,
	]);

	const removeNodeHandler = useCallback(() => {
		void callValidation((rules?: IFlatTreeValidationRule[]) => {
			const isMandatory = rules?.some((rule) => {
				return rule.type.name === EValidationRuleType.MANDATORY && rule.isMandatory;
			});

			if (!isMandatory) {
				onRemove(node);
				setIsError(false);

				return;
			}

			setIsError(true);
		});
	}, [node, onRemove, callValidation]);

	const enterAddMode = useCallback(() => {
		void callValidation(() => setIsAddMode(true));
	}, [callValidation]);

	const exitAddMode = useCallback(() => setIsAddMode(false), []);

	const enterEditMode = useCallback(() => {
		void callValidation(() => setIsEditMode(true));
	}, [callValidation]);

	const exitEditMode = useCallback(() => setIsEditMode(false), []);

	const usedKeys = useMemo(() => {
		return getChildrenNamesList ? getChildrenNamesList(node) : [];
	}, [getChildrenNamesList, node]);

	const availableValues = useMemo(() => {
		return getChildrenNamesList && nodeValidation?.availableValues
			? nodeValidation?.availableValues.filter((item) => !usedKeys.includes(item))
			: [];
	}, [getChildrenNamesList, nodeValidation, usedKeys]);

	const parentNodeValidation = useMemo(() => {
		return nodeValidation ?
			{
				...nodeValidation,
				availableValues,
			} : {};
	}, [availableValues, nodeValidation]);

	const isNoKeysAvailable = usedKeys.length > 0
		&& nodeValidation?.availableValues?.length !== 0
		&& availableValues.length === 0;

	return (
		<>
			<div style={{ ...style }}>
				<div
					className={styles.nodeWrapper}
					style={{ paddingLeft: paddingSize }}
				>
					{!isReadOnly && isEditMode
						? (
							<EditFlatNodeForm
								exitEditMode={exitEditMode}
								node={node}
								nodeValidation={nodeValidation}
								onSubmit={onEdit}
							/>
						)
						: (
							<FlatTreeNode
								brackets={node.isHiddenChildren
									? { openedBracket, closedBracket }
									: { openedBracket }
								}
								copyNodeHandler={copyNodeHandler}
								enterAddMode={enterAddMode}
								enterEditMode={enterEditMode}
								isCollapsed={node.isHiddenChildren}
								isReadOnly={isReadOnly}
								node={node}
								removeNodeHandler={removeNodeHandler}
								skipNodeHandler={skipNodeHandler}
								toggleCollapse={toggleCollapse}
							/>
						)
					}
					{!isReadOnly && isAddMode && (
						<AddFlatNodeForm
							exitAddMode={exitAddMode}
							getValidationRules={getValidationRules}
							isArray={node.nodeType === ETreeTypeNode.array}
							isNoKeysAvailable={isNoKeysAvailable}
							node={node}
							onSubmit={onAdd}
							parentNodeValidation={parentNodeValidation}
							usedKeys={usedKeys}
						/>
					)}
				</div>
			</div>
			{isError && (
				<FlatTreeNodeError
					errorText='Field is mandatory'
					style={{ paddingLeft: node.depth * FLAT_TREE_NODE_PADDING + FLAT_TREE_CHECKBOX_WIDTH }}
				/>
			)}
			{node.brackets && node.brackets.map((item, index) => {
				const wrapperTopStyle = `calc(${style?.top}px + 15px)`;
				const bracketTopStyle = `${FLAT_TREE_NODE_VIRTUALIZED_DEFAULT_HEIGHT * (index)}px`;
				const key = `closing_bracket_${node.id}_${item.depth}_${index}`;

				return isWithVirtualization ? (
					<div
						key={key}
						style={{ ...style, top: wrapperTopStyle }}
					>
						<div
							className={styles.closingBracket}
							data-testid='closing_bracket'
							style={{
								paddingLeft: item.depth * FLAT_TREE_NODE_PADDING,
								position: 'relative',
								top: bracketTopStyle,
								left: '5px',
							}}
						>
							{item.bracket}
						</div>
					</div>
				) : (
					<div
						key={key}
						className={styles.closingBracket}
						data-testid='closing_bracket'
						style={{ paddingLeft: item.depth * FLAT_TREE_NODE_PADDING }}
					>
						{item.bracket}
					</div>
				);
			})}
		</>
	);
};

export default memo(FlatTreeNodeWrapper);
